import { useState } from "react";
import { ECharacterClass, preGenCharacters, TCharacter, TPlayer } from "../../data/character";
import './slider.css'
import './characterSelection.css'
import { TTown } from "../../data/town";
import { GetRandomNumber } from "../../helper/common";

interface ICharacterSelectionProps {
    characterOptions: TCharacter[];
    playerSelectedCharacter: (character: TCharacter, index: number) => void;
}


export default function CharacterSelection(props: ICharacterSelectionProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const quantity: number = props.characterOptions.length;

  const sliderStyle: React.CSSProperties =
    selectedIndex === null
      ? { "--quantity": quantity } as React.CSSProperties 
      : {
          "--quantity": quantity,
          animation: "none", 
          transform: `perspective(1200px) rotateX(-16deg) rotateY(${-selectedIndex * (360 / quantity)}deg)`,
          transition: "transform 0.5s ease-in-out", 
        } as React.CSSProperties;


  const handleCardClick = (index: number) => {
    setSelectedIndex(index);
  };


  const handleCloseDetail = () => {
    setSelectedIndex(null);
  };


  const handleNext = () => {
    if (selectedIndex !== null) {
      const nextIndex = (selectedIndex + 1) % quantity;
      setSelectedIndex(nextIndex);
    }
  };


  const handlePrev = () => {
    if (selectedIndex !== null) {
      const prevIndex = (selectedIndex - 1 + quantity) % quantity;
      setSelectedIndex(prevIndex);
    }
  };


  if (selectedIndex === null) {
    return (
      <div className="banner">
        <div className="slider" style={sliderStyle}>
          {props.characterOptions.map((character, index) => (
            <div
              key={index}
              className="item"
              style={{ "--position": index + 1, cursor: "pointer" } as React.CSSProperties}
              onClick={() => handleCardClick(index)}
            >
              <img
                src={`images/characterSelectionCards/${character.Name}.png`}
                alt={`Character ${character.Name}`}
              />
              <p>{character.Name}</p>
              <p>{character.Occupation}</p>
            </div>
          ))}
        </div>
      </div>
    );
  } else {
    const character = props.characterOptions[selectedIndex];
    return (
      <div className="character-detail-view">
        <div className="selected-card" onClick={handleCloseDetail} style={{ cursor: "pointer" }}>
          <img
            src={`images/characterSelectionCards/${character.Name}.png`}
            alt={`Character ${character.Name}`}
          />
        </div>
        <div className="detail-panel">
          <h2>{character.Name}</h2>
          <p>Occupation: {character.Occupation}</p>
          <p>{character.Description}</p> {/* Need to replace {town} with the start town name */}
          <p>That's your life. Or so you always thought. Always believed. Until now...</p> {/* Same for all characters. */}      
          <p>Skills: {"No skills data available"}</p>
          <p>Wayne Says: I will style this more once we have more stuff to put in, might do buttons before that tho</p>
          <div className="navigation-buttons">
            <button onClick={handlePrev}>Previous</button>
            <button onClick={handleNext}>Next</button>
          </div>
          <div className="actions">
            <button onClick={handleCloseDetail}>Back to Carousel</button>
            <button onClick={() => props.playerSelectedCharacter(character, selectedIndex)}>
              Play as {character.Name}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
    

/**
 * Generates a random selection of characters from predefined character classes.
 *
 * @param countPerClass - The number of characters to select from each class. Defaults to 1.
 * @returns An array of randomly selected characters, including the specified number from each class.
 *
 * The function filters predefined characters (`preGenCharacters`) by their class
 * (Melee, Shooter, Mastermind, Charmer) and selects a random subset from each class.
 * It uses the `getRandomElements` helper function to shuffle and pick the desired number
 * of characters from each filtered list.
 *
 * @example
 * // Get 2 random characters from each class
 * const characters = getRandomCharacters(2);
 * console.log(characters);
 */
export function getRandomCharacters(countPerClass: number = 1, gameTowns: TTown[]): TCharacter[] {
  function getRandomElements<T>(arr: T[], count: number): T[] {
    const shuffled = structuredClone(arr); 
    for (let i = shuffled.length - 1; i > 0; i--) {
    const j = GetRandomNumber(i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, count);
  }
  
  const meleeCharacters = preGenCharacters.filter(
    (character) => character.Class === ECharacterClass.Melee
  );
  const shooterCharacters = preGenCharacters.filter(
    (character) => character.Class === ECharacterClass.Shooter
  );
  const mastermindCharacters = preGenCharacters.filter(
    (character) => character.Class === ECharacterClass.Mastermind
  );
  const charmerCharacters = preGenCharacters.filter(
    (character) => character.Class === ECharacterClass.Charmer
  );
  
  const result: TCharacter[] = [];
  
  result.push(...getRandomElements(meleeCharacters, countPerClass));
  result.push(...getRandomElements(shooterCharacters, countPerClass));
  result.push(...getRandomElements(mastermindCharacters, countPerClass));
  result.push(...getRandomElements(charmerCharacters, countPerClass));
  
  result.forEach((character, index) => {
    const townName = gameTowns[index % gameTowns.length]?.Name || "Unknown Town";
    character.Description = character.Description.replace("{town}", townName);
  });
  
  return result;
  }
